import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { LogoMark } from './components/Logo';
import { AppShell } from './layout/AppShell';
import { ApiError } from './lib/api';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage, SignupPage } from './pages/AuthPages';
import { DashboardPage } from './pages/DashboardPage';
import { SettingsPage } from './pages/PlaceholderPage';
import { ProjectsPage } from './pages/ProjectsPage';
import './styles.css';

// Pages chargées à la demande : le premier affichage reste léger (réseaux mobiles).
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })));
const EquipmentPage = lazy(() => import('./pages/EquipmentPage').then((m) => ({ default: m.EquipmentPage })));
const LaborPage = lazy(() => import('./pages/LaborPage').then((m) => ({ default: m.LaborPage })));
const MaterialsPage = lazy(() => import('./pages/MaterialsPage').then((m) => ({ default: m.MaterialsPage })));
const HelpPage = lazy(() => import('./pages/HelpPage').then((m) => ({ default: m.HelpPage })));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage').then((m) => ({ default: m.SubscriptionPage })));
const PlatformPage = lazy(() => import('./pages/PlatformPage').then((m) => ({ default: m.PlatformPage })));
const AiPage = lazy(() => import('./pages/AiPage').then((m) => ({ default: m.AiPage })));
const ImportPage = lazy(() => import('./pages/ImportPage').then((m) => ({ default: m.ImportPage })));
const PlansPage = lazy(() => import('./pages/PlansPage').then((m) => ({ default: m.PlansPage })));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage').then((m) => ({ default: m.InvoicesPage })));
const InvoicePage = lazy(() => import('./pages/InvoicePage').then((m) => ({ default: m.InvoicePage })));
const NewInvoicePage = lazy(() => import('./pages/NewInvoicePage').then((m) => ({ default: m.NewInvoicePage })));
const PaymentMethodsPage = lazy(() => import('./pages/PaymentMethodsPage').then((m) => ({ default: m.PaymentMethodsPage })));
const QuotationsPage = lazy(() => import('./pages/QuotationsPage').then((m) => ({ default: m.QuotationsPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const MetrePage = lazy(() => import('./pages/SimpleCalcPage').then((m) => ({ default: m.MetrePage })));
const ProjectPage = lazy(() => import('./pages/project/ProjectPage').then((m) => ({ default: m.ProjectPage })));
const AnalysisPage = lazy(() => import('./pages/AnalysisPage').then((m) => ({ default: m.AnalysisPage })));
const ClientsPage = lazy(() => import('./pages/ClientsPage').then((m) => ({ default: m.ClientsPage })));
const CompanyPage = lazy(() => import('./pages/CompanyPage').then((m) => ({ default: m.CompanyPage })));
const LibraryDomainPage = lazy(() => import('./pages/LibraryDomainPage').then((m) => ({ default: m.LibraryDomainPage })));
const LibraryItemPage = lazy(() => import('./pages/LibraryItemPage').then((m) => ({ default: m.LibraryItemPage })));
const CalculatorPage = lazy(() => import('./pages/CalculatorPage').then((m) => ({ default: m.CalculatorPage })));
const PriceListsPage = lazy(() => import('./pages/PriceListsPage').then((m) => ({ default: m.PriceListsPage })));
const PriceListPage = lazy(() => import('./pages/PriceListsPage').then((m) => ({ default: m.PriceListPage })));
const LibraryPage = lazy(() => import('./pages/LibraryPage').then((m) => ({ default: m.LibraryPage })));
const QuickQuotePage = lazy(() => import('./pages/QuickQuotePage').then((m) => ({ default: m.QuickQuotePage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
    },
  },
});

function Splash() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <LogoMark className="h-12 w-12 animate-pulse" />
    </div>
  );
}

function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  return user ? children : <Navigate to="/connexion" replace />;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  return user ? <Navigate to="/" replace /> : children;
}

function App() {
  return (
    <Suspense fallback={<Splash />}>
    <Routes>
      <Route path="/connexion" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/inscription" element={<PublicOnly><SignupPage /></PublicOnly>} />
      <Route path="/mot-de-passe-oublie" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      <Route path="/mot-de-passe/:token" element={<ResetPasswordPage />} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route index element={<DashboardPage />} />
        <Route path="projets" element={<ProjectsPage />} />
        <Route path="projets/:id" element={<ProjectPage />} />
        <Route path="metre" element={<MetrePage />} />
        <Route path="materiaux" element={<MaterialsPage />} />
        <Route path="materiels" element={<EquipmentPage />} />
        <Route path="main-oeuvre" element={<LaborPage />} />
        <Route path="devis" element={<QuotationsPage />} />
        <Route path="devis/rapide" element={<QuickQuotePage />} />
        <Route path="factures" element={<InvoicesPage />} />
        <Route path="factures/nouvelle" element={<NewInvoicePage />} />
        <Route path="factures/:id" element={<InvoicePage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="bibliotheque" element={<LibraryPage />} />
        <Route path="bibliotheque/prix" element={<PriceListsPage />} />
        <Route path="bibliotheque/prix/:id" element={<PriceListPage />} />
        <Route path="bibliotheque/domaines/:code" element={<LibraryDomainPage />} />
        <Route path="bibliotheque/ouvrages/:code" element={<LibraryItemPage />} />
        <Route path="bibliotheque/calculateurs/:code" element={<CalculatorPage />} />
        <Route path="analyse" element={<AnalysisPage />} />
        <Route path="ai" element={<AiPage />} />
        <Route path="import" element={<ImportPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="rapports" element={<ReportsPage />} />
        <Route path="parametres" element={<SettingsPage />} />
        <Route path="parametres/entreprise" element={<CompanyPage />} />
        <Route path="parametres/paiements" element={<PaymentMethodsPage />} />
        <Route path="parametres/utilisateurs" element={<UsersPage />} />
        <Route path="parametres/audit" element={<AuditPage />} />
        <Route path="parametres/abonnement" element={<SubscriptionPage />} />
        <Route path="plateforme" element={<PlatformPage />} />
        <Route path="aide" element={<HelpPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
